import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Star, MessageSquare, ChevronLeft } from 'lucide-react';
import { ratingService } from '../services/ratingService';
import { NumberedPagination } from '../components/ui/numbered-pagination';
import { fetchUsers } from '../redux/slices/userSlice';
import Lottie from 'lottie-react';
import loadingAnimation from '../assets/Loading.json';

const UserRatings = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { users } = useSelector((state) => state.users);
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    totalCount: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  // İsmi capitalize et (Türkçe karakterlere uygun: i→İ, ı→I)
  const capitalizeName = (name) => {
    if (!name) return name;
    return name
      .toLocaleLowerCase('tr-TR')
      .split(' ')
      .map(word => word ? word.charAt(0).toLocaleUpperCase('tr-TR') + word.slice(1).toLocaleLowerCase('tr-TR') : '')
      .join(' ');
  };

  // Yorum için görüntülenecek ismi al - TC'ye karşılık gelen ismi kullanıcı listesinden bul
  const getRatingUserRecord = (rating) => {
    const tc = rating.identityNum || rating.tcNo || rating.tc;
    if (tc && users?.length) {
      return users.find(
        (u) =>
          String(u.tc) === String(tc) ||
          String(u.id) === String(tc) ||
          String(u.id) === String(rating.userId)
      );
    }
    if (rating.userId && users?.length) {
      return users.find(
        (u) => String(u.id) === String(rating.userId) || String(u.tc) === String(rating.userId)
      );
    }
    return null;
  };

  const getRatingRouteId = (rating) => {
    const user = getRatingUserRecord(rating);
    return user?.tc || user?.id || rating.identityNum || rating.tcNo || rating.tc || rating.userId || null;
  };

  const getRatingDisplayName = (rating) => {
    const name = rating.customerName
      || rating.fullName
      || (rating.firstName && rating.lastName ? `${rating.firstName} ${rating.lastName}`.trim() : null)
      || rating.firstName
      || rating.lastName;
    if (name) return capitalizeName(name);
    // TC'ye (identityNum) karşılık gelen kullanıcıyı bul
    const tc = rating.identityNum || rating.tcNo || rating.tc;
    if (tc && users?.length) {
      const user = users.find(u => String(u.tc) === String(tc));
      if (user?.name && user.name !== '-') return capitalizeName(user.name);
    }
    return 'Kullanıcı';
  };

  const fetchRatings = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      const result = await ratingService.getRatings({ page, limit: 20 });
      
      if (result.success) {
        setRatings(result.data || []);
        
        // Pagination bilgisini kontrol et ve set et
        if (result.pagination) {
          setPagination({
            page: result.pagination.page || page,
            limit: result.pagination.limit || 20,
            totalCount: result.pagination.totalCount ?? 0,
            totalPages: result.pagination.totalPages ?? 0,
            hasNextPage: result.pagination.hasNextPage ?? false,
            hasPreviousPage: result.pagination.hasPreviousPage ?? false,
          });
        }
      } else {
        setError(result.error || 'Yorumlar yüklenemedi');
      }
    } catch (err) {
      setError('Yorumlar yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    dispatch(fetchUsers({ force: false }));
  }, [dispatch]);

  useEffect(() => {
    fetchRatings(1);
  }, []);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      fetchRatings(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronLeft size={20} className="text-gray-600" />
          </button>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-50 rounded-lg text-yellow-600">
              <MessageSquare size={24} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Kullanıcı Yorumları</h1>
              <p className="text-sm text-gray-500 mt-1">Tüm kullanıcı değerlendirmeleri</p>
            </div>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && ratings.length === 0 && (
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
          <div className="flex items-center justify-center h-[400px]">
            <Lottie animationData={loadingAnimation} loop={true} className="w-32 h-32" />
          </div>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Ratings List */}
      {!loading && ratings.length > 0 && (
        <>
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="p-4">
            <div className="space-y-3">
              {ratings.map((rating) => {
                // 3 yıldız ve altı için açık kırmızı/pembe arka plan
                const isLowRating = rating.point <= 3;
                const displayName = getRatingDisplayName(rating);
                const routeId = getRatingRouteId(rating);
                return (
                <div
                  key={rating.id}
                  className={`p-4 rounded-xl transition-all duration-300 border ${
                    isLowRating 
                      ? 'bg-red-50 hover:bg-red-100 border-red-200' 
                      : 'bg-gray-50 hover:bg-gray-100 border-gray-200'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      {routeId ? (
                        <button
                          type="button"
                          onClick={() => navigate(`/kullanicilar/${routeId}`)}
                          className="text-base font-semibold text-gray-900 hover:text-black hover:underline"
                        >
                          {displayName}
                        </button>
                      ) : (
                        <span className="text-base font-semibold text-gray-900">
                          {displayName}
                        </span>
                      )}
                      <div className="flex items-center gap-1">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            size={20}
                            className={i < rating.point ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}
                          />
                        ))}
                      </div>
                    </div>
                    <span className="text-sm text-gray-500">
                      {new Date(rating.createdAt || rating.createdDate).toLocaleDateString('tr-TR', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                  {rating.comment && (
                    <p className="text-sm text-gray-700 leading-relaxed mt-3">
                      {rating.comment}
                    </p>
                  )}
                </div>
              );
              })}
            </div>
            </div>

            {/* Pagination — Müşteri listesi / bildirimler ile aynı NumberedPagination */}
            <div className="flex items-center justify-between gap-3 px-8 py-4 border-t border-gray-100 bg-gray-50/30">
              <p className="text-sm text-gray-500 font-medium min-w-max shrink-0 whitespace-nowrap">
                Toplam{' '}
                <span className="font-bold text-gray-900">{pagination.totalCount}</span> yorum
              </p>
              <NumberedPagination
                currentPage={pagination.page}
                totalPages={Math.max(1, pagination.totalPages || 1)}
                paginationItemsToDisplay={5}
                onPageChange={handlePageChange}
              />
            </div>
          </div>
        </>
      )}

      {/* Empty State */}
      {!loading && ratings.length === 0 && !error && (
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
          <div className="flex flex-col items-center justify-center h-[400px] text-gray-400">
            <MessageSquare size={64} className="mb-4 text-gray-200" />
            <p className="text-lg font-medium text-gray-500">Henüz yorum yok</p>
            <p className="text-sm text-gray-400 mt-2">Kullanıcılar henüz değerlendirme yapmamış</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserRatings;

